import type { JSX } from "react";

import { SearchX } from "lucide-react";

interface RecommendationEmptyStateProps {
  /** How many items exist before filtering — zero means there is nothing to show at all. */
  totalCount: number;
  /** Whether any filter is narrowing the list. */
  hasActiveFilter: boolean;
  /** Copy for a genuinely empty list, e.g. "No recommendations for this audit yet." */
  emptyMessage: string;
  onClear: () => void;
}

/**
 * The zero-result panel under a recommendations filter bar.
 *
 * Several orthogonal filters make an empty result far more reachable than a single pill row did, so
 * the view distinguishes "there is nothing here" from "your filters excluded everything" and offers
 * a way out rather than leaving a bare panel.
 */
const RecommendationEmptyState = ({
  totalCount,
  hasActiveFilter,
  emptyMessage,
  onClear,
}: RecommendationEmptyStateProps): JSX.Element => (
  <div className="flex flex-col items-center gap-3 py-12 text-center">
    <SearchX className="w-8 h-8 text-outline" />
    <p className="text-sm text-on-surface-variant">
      {totalCount === 0 ? emptyMessage : "No recommendations match these filters."}
    </p>
    {hasActiveFilter && (
      <button onClick={onClear} className="text-sm font-semibold text-primary hover:underline">
        Clear filters
      </button>
    )}
  </div>
);

export default RecommendationEmptyState;
